import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useEmailLogs } from '../../api/email';
import { EmailStatus, EmailStatusConfig, QueryEmailLogDto } from '../../types/email';

export function EmailLogsPage() {
  const [query, setQuery] = useState<QueryEmailLogDto>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [searchInput, setSearchInput] = useState('');
  const [selectedLog, setSelectedLog] = useState<string | null>(null);

  const { data, isLoading, error } = useEmailLogs(query);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQuery((prev) => ({ ...prev, search: searchInput || undefined, page: 1 }));
  };

  const handleStatusFilter = (status: EmailStatus | '') => {
    setQuery((prev) => ({ ...prev, status: status || undefined, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setQuery((prev) => ({ ...prev, page: newPage }));
  };

  const selectedLogData = selectedLog
    ? data?.data.find((log) => log.id === selectedLog)
    : null;

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Fehler beim Laden der E-Mail Logs</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gesendete E-Mails</h1>
          <p className="mt-1 text-sm text-gray-500">
            Protokoll aller gesendeten E-Mails
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Search */}
            <form onSubmit={handleSearch} className="sm:col-span-2">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Suche
              </label>
              <div className="flex">
                <input
                  type="text"
                  id="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Betreff, Empfaenger..."
                  className="flex-1 block w-full rounded-l-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-700 hover:bg-gray-100"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
            </form>

            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                value={query.status || ''}
                onChange={(e) => handleStatusFilter(e.target.value as EmailStatus | '')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="">Alle Status</option>
                {Object.entries(EmailStatusConfig).map(([value, config]) => (
                  <option key={value} value={value}>
                    {config.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Table */}
        <div className={`bg-white shadow rounded-lg overflow-hidden ${selectedLog ? 'flex-1' : 'w-full'}`}>
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Laden...</p>
            </div>
          ) : data?.data.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Keine E-Mails gefunden</h3>
              <p className="mt-1 text-sm text-gray-500">
                Es wurden noch keine E-Mails gesendet.
              </p>
            </div>
          ) : (
            <>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Betreff
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Empfaenger
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Datum
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.data.map((log) => {
                    const statusConfig = EmailStatusConfig[log.status];
                    return (
                      <tr
                        key={log.id}
                        className={`hover:bg-gray-50 cursor-pointer ${
                          selectedLog === log.id ? 'bg-primary-50' : ''
                        }`}
                        onClick={() => setSelectedLog(selectedLog === log.id ? null : log.id)}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                            {log.subject}
                          </div>
                          {log.bewerber && (
                            <div className="text-xs text-gray-500">
                              <Link
                                to={`/bewerber/${log.bewerber.id}`}
                                className="text-primary-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {log.bewerber.firstName} {log.bewerber.lastName}
                              </Link>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {log.recipients.slice(0, 2).join(', ')}
                            {log.recipients.length > 2 && ` +${log.recipients.length - 2}`}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color}`}
                          >
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(log.createdAt).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {data?.meta && data.meta.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(data.meta!.page - 1)}
                      disabled={data.meta!.page <= 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Zurueck
                    </button>
                    <button
                      onClick={() => handlePageChange(data.meta!.page + 1)}
                      disabled={data.meta!.page >= data.meta!.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Weiter
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <p className="text-sm text-gray-700">
                      Zeige{' '}
                      <span className="font-medium">{(data.meta.page - 1) * data.meta.limit + 1}</span>
                      {' '}-{' '}
                      <span className="font-medium">
                        {Math.min(data.meta.page * data.meta.limit, data.meta.total)}
                      </span>
                      {' '}von{' '}
                      <span className="font-medium">{data.meta.total}</span>
                    </p>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(data.meta!.page - 1)}
                        disabled={data.meta!.page <= 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        Seite {data.meta.page} von {data.meta.totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(data.meta!.page + 1)}
                        disabled={data.meta!.page >= data.meta!.totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Detail Panel */}
        {selectedLogData && (
          <div className="w-96 bg-white shadow rounded-lg overflow-hidden flex-shrink-0">
            <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">E-Mail Details</h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">Betreff</label>
                <p className="mt-1 text-sm text-gray-900">{selectedLogData.subject}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">An</label>
                <p className="mt-1 text-sm text-gray-900">{selectedLogData.recipients.join(', ')}</p>
              </div>
              {selectedLogData.ccRecipients.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">CC</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLogData.ccRecipients.join(', ')}</p>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">Status</label>
                <span
                  className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    EmailStatusConfig[selectedLogData.status].bgColor
                  } ${EmailStatusConfig[selectedLogData.status].color}`}
                >
                  {EmailStatusConfig[selectedLogData.status].label}
                </span>
              </div>
              {selectedLogData.error && (
                <div>
                  <label className="block text-xs font-medium text-red-500 uppercase">Fehler</label>
                  <p className="mt-1 text-sm text-red-600">{selectedLogData.error}</p>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">Gesendet am</label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedLogData.sentAt
                    ? new Date(selectedLogData.sentAt).toLocaleString('de-DE')
                    : 'Nicht gesendet'}
                </p>
              </div>
              {selectedLogData.template && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Vorlage</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLogData.template.name}</p>
                </div>
              )}
              {selectedLogData.createdBy && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Gesendet von</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedLogData.createdBy.firstName} {selectedLogData.createdBy.lastName}
                  </p>
                </div>
              )}
              <div className="pt-4 border-t">
                <label className="block text-xs font-medium text-gray-500 uppercase mb-2">Inhalt</label>
                <div
                  className="prose prose-sm max-w-none bg-gray-50 rounded-md p-4 max-h-64 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: selectedLogData.bodyHtml }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
